import { useState, useEffect } from 'react';
import { User, Clock, Calendar, CheckCircle, XCircle } from 'lucide-react';
import { useApi } from '../../../../hooks/useApi';
import { TIME_SLOTS } from '../constants';

export const CounselorSchedule = () => {
  const { get } = useApi();
  const [counselors, setCounselors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  const fetchCounselors = async () => {
    try {
      setLoading(true);
      const response = await get('/users/counselors');
      if (response.data?.status === 'success') {
        const counselorsWithSchedule = await Promise.all(
          response.data.data.map(async (counselor) => {
            const scheduleResponse = await get('/appointments/available-slots', {
              params: {
                counselorId: counselor._id,
                date: selectedDate
              }
            });
            
            return {
              ...counselor,
              availableSlots: scheduleResponse.data?.data?.slots || []
            };
          })
        );
        setCounselors(counselorsWithSchedule);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCounselors();
  }, [selectedDate]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-1/4"></div>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-red-600">Failed to load counselor schedules</div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b">
        <h2 className="text-lg font-medium">Counselor Schedules</h2>
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700">
            Select Date
          </label>
          <input
            type="date"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-uzima-maroon-400 focus:ring-uzima-maroon-400"
            value={selectedDate}
            min={new Date().toISOString().split('T')[0]}
            onChange={(e) => setSelectedDate(e.target.value)}
          />
        </div>
      </div>

      <div className="divide-y">
        {counselors.map((counselor) => (
          <div key={counselor._id} className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <User className="h-5 w-5 text-gray-400 mr-2" />
                <div>
                  <h3 className="font-medium">{counselor.fullName}</h3>
                  <p className="text-sm text-gray-500">
                    {counselor.availableSlots.length} available slots
                  </p>
                </div>
              </div>
              <span className={`px-2 py-1 text-xs rounded-full ${
                counselor.isActive 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-red-100 text-red-800'
              }`}>
                {/* {counselor.isActive ? 'Available' : 'Unavailable'} */}
              </span>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2">
              {TIME_SLOTS.map((slot) => {
                const isAvailable = counselor.availableSlots.includes(slot);
                return (
                  <div
                    key={slot}
                    className={`p-2 rounded-md text-sm flex items-center justify-between ${
                      isAvailable 
                        ? 'bg-green-50 text-green-700' 
                        : 'bg-gray-50 text-gray-500'
                    }`}
                  >
                    <span>{slot}</span>
                    {isAvailable ? (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : (
                      <XCircle className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};